import { Injectable, inject } from '@angular/core';
import { Auth, AuthCredential, EmailAuthProvider, User, getAuth, reauthenticateWithCredential, sendPasswordResetEmail, signInWithEmailAndPassword, updateEmail, user } from '@angular/fire/auth';
import { Firestore, docData } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { doc } from 'firebase/firestore';
import { Observable, Subscription, map, shareReplay } from 'rxjs';
import { User as UserModel } from 'sap';

import { CollectionsService } from './collections.service';
import { FirestoreUtilsService } from './firestore-utils.service';
import { UserStatusService } from './user-status.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private router: Router = inject(Router);
  private collectionsService = inject(CollectionsService);
  private firestoreUtilsService = inject(FirestoreUtilsService);
  private firestore = inject(Firestore);
  private userStatusService = inject(UserStatusService);

  user$ = user(this.auth);
  userSubscription: Subscription;
  userId!: string;

  public overlay_signOut = false;

  // Change Email State Management
  public overlay_reauthenticate = false;
  public showReauthenticateMessage = true;
  public showNewEmail = false;
  public showEmailChangeSuccess = false;

  constructor() {
    this.userSubscription = this.user$.subscribe((aUser: User | null) => {
      //handle user state changes here. Note, that user will be null if there is no currently logged in user.
      const previousUserId = this.userId;
      const user = aUser;
      const userId = user?.uid ?? '';

      if (previousUserId !== userId) {
        this.userStatusService.clearUserStatus();
      }

      this.userId = userId;
    });
  }

  async emailLogin(email: string, password: string): Promise<void> {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then(() => {
        this.router.navigate(['/folders']);
      })
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Get User Id
   */
  getUserId() {
    return this.user$;
  }

  /**
   * Get User Doc
   * @param userId
   * @returns
   */

  public getUserDoc$(userId: string): Observable<UserModel | null> {
    const path = `${this.collectionsService.usersCol()}/${userId}`;
    const docRef = doc(this.firestore, path);
    return docData(docRef, { idField: 'id' }).pipe(
      map((data) => (data ? (data as UserModel) : null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Update User Doc
   * @param form
   * @param userId
   */
  public updateUserDoc(form: UserModel, userId: string) {
    const userPath = `${this.collectionsService.usersCol()}/${userId}`;
    return this.firestoreUtilsService.setDocumentData<UserModel>(userPath, form);
  }

  /**
   * Update User Email
   * @param email
   * @param userId
   */
  public updateUserEmail(email: string, userId: string) {
    const userPath = `${this.collectionsService.usersCol()}/${userId}`;
    const user = {
      user_email: email,
    };
    return this.firestoreUtilsService.setPartialDocumentData<UserModel>(userPath, user);
  }

  /**
   * Reauthenticate
   * Reauthenticates the user with the provided email and password.
   * Updates the overlay state, showing the new email form upon successful reauthentication.
   * Handles errors by displaying an alert.
   *
   * @param email - The user's email.
   * @param password - The user's password.
   */
  reauthenticate(email: string, password: string): void {
    const auth = getAuth();
    const user = auth.currentUser;
    const credential: AuthCredential = EmailAuthProvider.credential(email, password);

    reauthenticateWithCredential(user!, credential)
      .then(() => {
        // User re-authenticated successfully.
        this.overlay_reauthenticate = false;
        this.showNewEmail = true;
        this.showReauthenticateMessage = false;
      })
      .catch((error) => {
        // An error occurred during reauthentication.
        // Display an alert with the error message.
        alert(error);
      });
  }

  /**
   * Update Email
   * Updates the user's email with the provided new email.
   * Updates the overlay state, showing success or error messages.
   * Handles errors by displaying an alert.
   *
   * @param email - The new email to update.
   */
  updateEmail(email: string): void {
    const auth = getAuth();
    const user = auth.currentUser;

    updateEmail(user!, email)
      .then(async () => {
        if (user) {
          await this.updateUserEmail(email, user.uid);
          // Email updated successfully.
          this.overlay_reauthenticate = false;
          this.showNewEmail = false;
          this.showEmailChangeSuccess = true;

          // Reset success message after a delay.
          setTimeout(() => {
            this.showEmailChangeSuccess = false;
            this.showReauthenticateMessage = true;
          }, 3000);
        }
      })
      .catch((error) => {
        // An error occurred during email update.
        // Display an alert with the error message.
        alert(error);
      });
  }

  /**
   * Send Password Reset Email
   * Sends a password reset email to the specified email address.
   *
   * @param email - The email address to send the reset link to.
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    const auth = getAuth();
    return sendPasswordResetEmail(auth, email)
      .then(() => {
        alert('Password reset email sent successfully! Please check your email.');
      })
      .catch((error) => {
        alert(`Error sending password reset email: ${error.message}`);
      });
  }

  /**
   * Sign Out
   */
  async signOut() {
    this.userStatusService.clearUserStatus();
    await this.auth.signOut();
    this.router.navigate(['/login']);
  }
}
